import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { TrashIcon } from '@heroicons/react/24/solid'
import { Badge, DownloadButton, Pagination, Spinner } from '@/components/ui'
import { formatDate } from '@/utils/formatters'
import apiFetch from '@/services/apiService'
import { useContactContext } from '../ContactShow'
import { deleteDocument } from '../../documents/documentsSlice'
import { addFlashMessage } from '../../flash/flashMessagesSlice'

const DocumentsList = ({ type, title }) => {
  const dispatch = useDispatch()
  const { contact } = useContactContext()
  
  // États pour gérer les documents
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  })
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    type_document: type
  })
  
  // États pour la confirmation de suppression
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)
  
  // Fonction pour charger les documents
  const fetchDocuments = async () => {
    if (!contact?.id) return
    
    setLoading(true)
    try {
      const response = await apiFetch()
        .url(`contacts/${contact.id}/documents`)
        .query(filters)
        .get()
      
      setDocuments(response.documents || [])
      setPagination({
        currentPage: response.current_page || 1,
        totalPages: response.total_pages || 1,
        totalCount: response.total_count || 0
      })
    } catch (error) {
      console.error(`Erreur lors du chargement des documents de type ${type}:`, error)
      dispatch(addFlashMessage({
        type: 'error',
        text: `Erreur lors du chargement des documents de type ${type}`
      }))
    } finally {
      setLoading(false)
    }
  }
  
  // Charger les documents au chargement du composant et lorsque les filtres changent
  useEffect(() => {
    fetchDocuments()
  }, [contact?.id, filters])
  
  // Gérer le changement de page
  const handlePageChange = (page) => {
    setFilters({ ...filters, page })
  }
  
  // Gérer la suppression d'un document
  const handleDelete = (document) => {
    setSelectedDocument(document)
    setShowConfirmDialog(true)
  }
  
  // Confirmer la suppression
  const confirmDelete = () => {
    dispatch(deleteDocument(selectedDocument))
    setSelectedDocument(null)
    setShowConfirmDialog(false)
    // Recharger les documents après la suppression
    setTimeout(fetchDocuments, 500)
  }
  
  // Annuler la suppression
  const cancelDelete = () => {
    setSelectedDocument(null)
    setShowConfirmDialog(false)
  }
  
  // Si aucun document n'est trouvé, ne pas afficher le composant
  if (!loading && documents.length === 0) {
    return null
  }
  
  return (
    <div className="min-h-[200px] mb-8">
      <div className="flex flex-col mb-4">
        <h2 className="text-xl font-bold">{title}</h2>
      </div>
      <div className="grid grid-cols-2 gap-4 bg-gray-100 p-6 rounded-none transition-all duration-1000">            
        { loading && <div className="w-full text-center"><Spinner color='gray-600'/></div> }
        { !loading && documents.map((document, index) => (
          <div data-id={document.id} data-model="document" key={index} className="flex space-x-2 hover:bg-primary-200 p-2 rounded-sm">
            { document.file && <DownloadButton file={document.file} title={document.titre}/> }
            <div className="flex flex-col">
              <div className="flex">
                <span>{document.titre}</span>
                <button 
                  className="ml-2 btn btn-xs btn-ghost btn-square p-1 hover:bg-primary-400"
                  arial-label="Supprimer le document"
                  title="Supprimer le document"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDelete(document)
                  }
                }>
                  <TrashIcon className="w-4 h-4"/>
                </button>
              </div>
              <div className="flex gap-2">
                <Badge text={`Disponible depuis le ${formatDate(document.date_disponibilite)}`} />
              </div>
            </div>
          </div> 
        ))}
      </div>
      {pagination.totalPages > 1 && (
        <Pagination 
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      )}
      {showConfirmDialog && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmer la suppression</h3>
            <p className="py-4">Êtes-vous sûr de vouloir supprimer le document "{selectedDocument.titre}" ?</p>
            <div className="modal-action">
              <button onClick={confirmDelete} className="btn btn-secondary">Supprimer</button>
              <button onClick={cancelDelete} className="btn btn-outline">Annuler</button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  )
}

export default DocumentsList